import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import classNames from 'classnames';
import * as React from 'react';
import FocusLock from 'react-focus-lock';
import { useId } from 'react-id-generator';
import styled from 'styled-components';
import { KEYS } from '../../constants/keys.js';
import { useControlledDropdown } from '../../hooks/use_controlled_dropdown.js';
import { useTranslations } from '../../hooks/use_translations.js';
import { spacing1x } from '../../styles/spacing.js';
import { useThemeContext } from '../../styles/themes/themes.js';
import { isPlainObject } from '../../utils/lang.js';
import { bindRefs } from '../../utils/react.js';
import { noop } from '../../utils/util.js';
import { Badge } from '../Badge/Badge.js';
import { Box } from '../Box/Box.js';
import { DropdownBase } from '../DropdownBase/DropdownBase.js';
import { IconSearch } from '../Icons/IconSearch.js';
import { Input } from '../Input/Input.js';
import { Listbox } from '../Listbox/Listbox.js';
import { SelectButton } from './SelectButton.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';

var _excluded = ["aria-labelledby", "autoWidth", "buttonProps", "className", "containerSelector", "defaultOption", "disabled", "emailStyle", "enableSelectAll", "endAdornment", "error", "footer", "id", "inline", "lightStyle", "multi", "onChange", "onHide", "onShow", "optionHeight", "options", "readOnly", "renderButton", "renderLabel", "renderOption", "searchable", "searchFilter", "showing", "startAdornment", "value"];
var MAX_HEIGHT = 490;
var HeaderContainer = styled.div.withConfig({
  displayName: "Select__HeaderContainer",
  componentId: "sc-besirf-0"
})(["&&{border-bottom:1px solid ", ";padding:5px;}&&:focus{outline:0;}"], function (_ref) {
  var theme = _ref.theme;
  return theme.neutralColors.silver;
});
var FooterWrapper = styled.div.withConfig({
  displayName: "Select__FooterWrapper",
  componentId: "sc-besirf-1"
})(["&&{border-top:", ";}"], function (_ref2) {
  var theme = _ref2.theme;
  return "solid 1px " + theme.neutralColors.silver;
});
function defaultSearchFilter(value, option) {
  var searchValue = "".concat(option.label || option.value || "").toLowerCase();
  return searchValue.includes(value.toLowerCase());
}
function defaultRenderOption(option) {
  return option.label || option.value || "";
}
function renderMultiSelectLabel(selectedOptions) {
  var arr = selectedOptions.map(defaultRenderOption);
  if (arr.length === 1) return arr[0];
  if (arr.length === 2) return "".concat(arr[0], " and ").concat(arr[1]);
  if (arr.length < 9) return arr.slice(0, arr.length - 1).join(", ") + ", and " + arr[arr.length - 1];
  var first9 = arr.slice(0, 10);
  return first9.join(", ") + ", and ".concat(arr.length - 9, " more");
}
var getValueArr = function getValueArr(valueProp) {
  if (valueProp == void 0) return [];
  function stringify(val) {
    return isPlainObject(val) && "value" in val && (typeof val.value === "string" || typeof val.value === "number" || typeof val.value === "boolean") ? val.value : val;
  }
  return Array.isArray(valueProp) ? valueProp.map(stringify) : [stringify(valueProp)];
};
var Select = /*#__PURE__*/React.forwardRef(function Select2(props, elRef) {
  var ariaLabelledBy = props["aria-labelledby"],
    autoWidth = props.autoWidth,
    buttonProps = props.buttonProps,
    className = props.className,
    containerSelector = props.containerSelector,
    defaultOption = props.defaultOption,
    disabled = props.disabled,
    _props$emailStyle = props.emailStyle,
    emailStyle = _props$emailStyle === void 0 ? false : _props$emailStyle,
    enableSelectAll = props.enableSelectAll,
    endAdornment = props.endAdornment,
    error = props.error,
    footer = props.footer,
    id = props.id,
    _props$inline = props.inline,
    inline = _props$inline === void 0 ? true : _props$inline,
    _props$lightStyle = props.lightStyle,
    lightStyle = _props$lightStyle === void 0 ? false : _props$lightStyle,
    multi = props.multi,
    onChange = props.onChange,
    _props$onHide = props.onHide,
    onHide = _props$onHide === void 0 ? noop : _props$onHide,
    _props$onShow = props.onShow,
    onShow = _props$onShow === void 0 ? noop : _props$onShow,
    optionHeight = props.optionHeight,
    options = props.options,
    _props$readOnly = props.readOnly,
    readOnly = _props$readOnly === void 0 ? false : _props$readOnly,
    renderButton = props.renderButton,
    _renderLabel = props.renderLabel,
    _props$renderOption = props.renderOption,
    renderOption = _props$renderOption === void 0 ? defaultRenderOption : _props$renderOption,
    searchableProp = props.searchable,
    _props$searchFilter = props.searchFilter,
    searchFilter = _props$searchFilter === void 0 ? defaultSearchFilter : _props$searchFilter,
    showing = props.showing,
    startAdornment = props.startAdornment,
    _props$value = props.value,
    valueProp = _props$value === void 0 ? [] : _props$value,
    rest = _objectWithoutProperties(props, _excluded);
  var _useThemeContext = useThemeContext(),
    localization = _useThemeContext.localization;
  var _useTranslations = useTranslations(localization === null || localization === void 0 ? void 0 : localization.translations),
    t = _useTranslations.t;
  var DEFAULT_VALUE = {
    label: t("Select.noSelection"),
    value: "NO_SELECTION"
  };
  var _useControlledDropdow = useControlledDropdown({
      showing: showing
    }),
    setShowingState = _useControlledDropdow.setShowingState,
    showingState = _useControlledDropdow.showingState;
  var _React$useState = React.useState(true),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    shouldFocusButton = _React$useState2[0],
    setShouldFocusButton = _React$useState2[1];
  var searchable = searchableProp && options.length > 1;
  var _React$useState3 = React.useState(""),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    inputValue = _React$useState4[0],
    setInputValue = _React$useState4[1];
  var nextValueArr = getValueArr(valueProp);
  var _React$useState5 = React.useState(nextValueArr),
    _React$useState6 = _slicedToArray(_React$useState5, 2),
    valueState = _React$useState6[0],
    setValueState = _React$useState6[1];
  var nextValueHash = nextValueArr.join(",");
  var valueHash = React.useRef(nextValueHash);
  var staleValue = valueHash.current !== nextValueHash;
  var value = staleValue ? nextValueArr : valueState;
  var selectedOptions = options.filter(function (option) {
    return value.includes(option.value);
  });
  React.useEffect(function () {
    if (!staleValue) return;
    setValueState(nextValueArr);
    valueHash.current = nextValueHash;
  }, [nextValueHash, nextValueArr, staleValue]);
  var buttonRef = React.useRef(null);
  var boxRef = React.useRef(null);
  var listRef = React.useRef(null);
  var inputRef = React.useRef(null);
  var footerRef = React.useRef(null);
  var _useId = useId(1, "listbox-"),
    _useId2 = _slicedToArray(_useId, 1),
    listboxId = _useId2[0];
  var _React$useState7 = React.useState(0),
    _React$useState8 = _slicedToArray(_React$useState7, 2),
    footerCollectionIndex = _React$useState8[0],
    setFooterCollectionIndex = _React$useState8[1];
  var filterOptions = function filterOptions(option) {
    return searchFilter(inputValue || "", option);
  };
  var handleHide = function handleHide() {
    var _buttonRef$current;
    if (!shouldFocusButton) setShouldFocusButton(true);else (_buttonRef$current = buttonRef.current) === null || _buttonRef$current === void 0 ? void 0 : _buttonRef$current.focus();
    onHide();
  };
  var handleShow = function handleShow() {
    var _listRef$current, _inputRef$current;
    if (!searchable) (_listRef$current = listRef.current) === null || _listRef$current === void 0 ? void 0 : _listRef$current.focus();else (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.focus();
    onShow();
  };
  var handleButtonClick = function handleButtonClick(e) {
    setShowingState(!showingState);
    if (buttonProps !== null && buttonProps !== void 0 && buttonProps.onClick) buttonProps.onClick(e);
  };
  var handleInputChange = function handleInputChange(e) {
    setInputValue(e.target.value);
  };
  var handleListboxChange = function handleListboxChange(nextSelectedOptions) {
    var nextSelectedValues = nextSelectedOptions.map(function (option) {
      return option.value;
    });
    setValueState(nextSelectedValues);
    if (onChange) onChange(multi ? nextSelectedOptions : nextSelectedOptions[0]);
    if (!multi) setShowingState(false);
  };
  var handleBoxKeyDown = function handleBoxKeyDown(e) {
    switch (e.key) {
      case KEYS.TAB:
        {
          setShowingState(false);
          setShouldFocusButton(false);
        }
    }
  };
  var handleInputKeyDown = function handleInputKeyDown(e) {
    switch (e.key) {
      case KEYS.ARROW_DOWN:
        {
          var _listRef$current2;
          (_listRef$current2 = listRef.current) === null || _listRef$current2 === void 0 ? void 0 : _listRef$current2.focus();
          e.preventDefault();
          break;
        }
      case KEYS.ESCAPE:
        {
          setShowingState(false);
          e.stopPropagation();
          break;
        }
      case KEYS.ARROW_UP:
        {
          if (footer) {
            var _footerRef$current, _footerRef$current2;
            var footerCollection = (_footerRef$current = footerRef.current) === null || _footerRef$current === void 0 ? void 0 : _footerRef$current.childNodes[0].childNodes;
            if (footerCollection) {
              setFooterCollectionIndex(footerCollection.length - 1);
            }
            var lastFooterItem = (_footerRef$current2 = footerRef.current) === null || _footerRef$current2 === void 0 ? void 0 : _footerRef$current2.childNodes[0].lastChild;
            lastFooterItem.focus();
          } else {
            var _listRef$current3;
            var lastItem = (_listRef$current3 = listRef.current) === null || _listRef$current3 === void 0 ? void 0 : _listRef$current3.lastChild;
            lastItem.focus();
          }
          e.preventDefault();
          break;
        }
    }
  };
  var handleListboxKeyDown = function handleListboxKeyDown(e) {
    switch (e.key) {
      case KEYS.ESCAPE:
        if (searchable) {
          var _inputRef$current2;
          setInputValue("");
          (_inputRef$current2 = inputRef.current) === null || _inputRef$current2 === void 0 ? void 0 : _inputRef$current2.focus();
          e.preventDefault();
        } else {
          setShowingState(false);
        }
        e.stopPropagation();
        break;
    }
  };
  var handleFinalListItem = function handleFinalListItem() {
    if (footer) {
      var _footerRef$current3;
      var firstChild = (_footerRef$current3 = footerRef.current) === null || _footerRef$current3 === void 0 ? void 0 : _footerRef$current3.childNodes[0].childNodes[0];
      if (firstChild) {
        firstChild.focus();
      } else {
        var _footerRef$current4;
        (_footerRef$current4 = footerRef.current) === null || _footerRef$current4 === void 0 ? void 0 : _footerRef$current4.focus();
      }
    } else if (searchable) {
      var _inputRef$current3;
      (_inputRef$current3 = inputRef.current) === null || _inputRef$current3 === void 0 ? void 0 : _inputRef$current3.focus();
    } else {
      var _listRef$current4;
      (_listRef$current4 = listRef.current) === null || _listRef$current4 === void 0 ? void 0 : _listRef$current4.focus();
    }
  };
  var handleFirstListItem = function handleFirstListItem() {
    if (searchable) {
      var _inputRef$current4;
      (_inputRef$current4 = inputRef.current) === null || _inputRef$current4 === void 0 ? void 0 : _inputRef$current4.focus();
    } else if (footer) {
      var _footerRef$current5, _footerRef$current6;
      var footerCollection = (_footerRef$current5 = footerRef.current) === null || _footerRef$current5 === void 0 ? void 0 : _footerRef$current5.childNodes[0].childNodes;
      if (footerCollection) {
        setFooterCollectionIndex(footerCollection.length - 1);
      }
      var lastFooterItem = (_footerRef$current6 = footerRef.current) === null || _footerRef$current6 === void 0 ? void 0 : _footerRef$current6.childNodes[0].lastChild;
      lastFooterItem.focus();
    } else {
      var _listRef$current5;
      var lastItem = (_listRef$current5 = listRef.current) === null || _listRef$current5 === void 0 ? void 0 : _listRef$current5.lastChild;
      lastItem.focus();
    }
  };
  var handleFooterNavigation = function handleFooterNavigation(e) {
    var _footerRef$current7, _listRef$current6, _footerRef$current8;
    switch (e.key) {
      case KEYS.ARROW_UP:
        var footerCollectionUp = (_footerRef$current7 = footerRef.current) === null || _footerRef$current7 === void 0 ? void 0 : _footerRef$current7.childNodes[0].childNodes;
        var nextElementUp = footerCollectionUp && footerCollectionUp[footerCollectionIndex - 1];
        var lastItem = (_listRef$current6 = listRef.current) === null || _listRef$current6 === void 0 ? void 0 : _listRef$current6.lastChild;
        if (footerCollectionUp) {
          if (footerCollectionIndex === 0) {
            lastItem.focus();
            setFooterCollectionIndex(0);
          } else {
            nextElementUp === null || nextElementUp === void 0 ? void 0 : nextElementUp.focus();
            setFooterCollectionIndex(footerCollectionIndex - 1);
          }
        }
        e.preventDefault();
        break;
      case KEYS.ARROW_DOWN:
        var footerCollectionDown = (_footerRef$current8 = footerRef.current) === null || _footerRef$current8 === void 0 ? void 0 : _footerRef$current8.childNodes[0].childNodes;
        var nextElementDown = footerCollectionDown && footerCollectionDown[footerCollectionIndex + 1];
        if (footerCollectionDown) {
          if (footerCollectionIndex === (footerCollectionDown === null || footerCollectionDown === void 0 ? void 0 : footerCollectionDown.length) - 1) {
            if (searchable) {
              var _inputRef$current5;
              (_inputRef$current5 = inputRef.current) === null || _inputRef$current5 === void 0 ? void 0 : _inputRef$current5.focus();
            } else {
              var _listRef$current7;
              (_listRef$current7 = listRef.current) === null || _listRef$current7 === void 0 ? void 0 : _listRef$current7.focus();
            }
            setFooterCollectionIndex(0);
          } else if (footerCollectionIndex < (footerCollectionDown === null || footerCollectionDown === void 0 ? void 0 : footerCollectionDown.length)) {
            nextElementDown === null || nextElementDown === void 0 ? void 0 : nextElementDown.focus();
            setFooterCollectionIndex(footerCollectionIndex + 1);
          }
        }
        e.preventDefault();
        break;
      case KEYS.ESCAPE:
        if (searchable) {
          var _inputRef$current6;
          setInputValue("");
          (_inputRef$current6 = inputRef.current) === null || _inputRef$current6 === void 0 ? void 0 : _inputRef$current6.focus();
          e.preventDefault();
        } else {
          setShowingState(false);
        }
    }
  };
  return /*#__PURE__*/jsx("div", _objectSpread(_objectSpread({}, rest), {}, {
    className: classNames("_mntl-select", className),
    ref: elRef,
    tabIndex: -1,
    children: /*#__PURE__*/jsx(DropdownBase, {
      inline: inline,
      maxHeight: MAX_HEIGHT,
      showing: showingState,
      onHide: handleHide,
      onShow: handleShow,
      onOutsideClick: function onOutsideClick() {
        return setShowingState(false);
      },
      containerSelector: containerSelector,
      renderTrigger: function renderTrigger(_ref3) {
        var ref = _ref3.ref;
        var selection = multi ? selectedOptions : selectedOptions[0];
        var baseProps = {
          "aria-expanded": showingState,
          "aria-haspopup": "listbox",
          "aria-labelledby": ariaLabelledBy,
          disabled: disabled,
          error: error,
          onClick: handleButtonClick,
          ref: bindRefs(ref, buttonRef),
          selection: selection
        };
        if (typeof renderButton === "function") {
          return renderButton(baseProps);
        }
        delete baseProps.selection;
        return /*#__PURE__*/jsx(SelectButton, _objectSpread(_objectSpread(_objectSpread({}, buttonProps), baseProps), {}, {
          emailStyle: emailStyle,
          id: id,
          lightStyle: lightStyle,
          readOnly: readOnly,
          renderLabel: function renderLabel() {
            var count = selectedOptions.length;
            if (_renderLabel) return _renderLabel(selection);
            if (!multi) return defaultRenderOption(selectedOptions[0] || defaultOption || DEFAULT_VALUE);
            return /*#__PURE__*/jsxs(Fragment, {
              children: [count ? /*#__PURE__*/jsx(Badge, {
                style: {
                  marginInlineEnd: "10px"
                },
                count: count,
                selected: true,
                role: "none"
              }) : null, renderMultiSelectLabel(count ? selectedOptions : [defaultOption || DEFAULT_VALUE])]
            });
          }
        }));
      },
      renderContent: function renderContent(_ref4) {
        var _triggerRef$current, _triggerRef$current2;
        var ref = _ref4.ref,
          style = _ref4.style,
          triggerRef = _ref4.triggerRef;
        return /*#__PURE__*/jsx(Box, {
          "aria-expanded": searchable ? true : void 0,
          "aria-haspopup": searchable ? "listbox" : void 0,
          elevation: 2,
          onKeyDown: handleBoxKeyDown,
          ref: bindRefs(ref, boxRef),
          role: searchable ? "combobox" : void 0,
          style: _objectSpread(_objectSpread({}, style), {}, {
            display: "flex",
            flexDirection: "column",
            borderRadius: "3px",
            height: "100%",
            marginTop: spacing1x,
            maxWidth: autoWidth && ((_triggerRef$current = triggerRef.current) === null || _triggerRef$current === void 0 ? void 0 : _triggerRef$current.offsetWidth),
            minWidth: (_triggerRef$current2 = triggerRef.current) === null || _triggerRef$current2 === void 0 ? void 0 : _triggerRef$current2.offsetWidth,
            overflow: "hidden",
            padding: 0,
            textAlign: "start",
            whiteSpace: "nowrap"
          }),
          children: /*#__PURE__*/jsxs(FocusLock, {
            autoFocus: false,
            disabled: !showingState,
            lockProps: {
              style: {
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                maxHeight: "100%"
              }
            },
            children: [searchable ? /*#__PURE__*/jsx(HeaderContainer, {
              children: /*#__PURE__*/jsx(Input, {
                "aria-label": "Search options",
                "aria-autocomplete": "list",
                "aria-controls": listboxId,
                startAdornment: IconSearch,
                onChange: handleInputChange,
                onKeyDown: handleInputKeyDown,
                ref: inputRef,
                style: {
                  width: "100%"
                },
                type: "text",
                value: inputValue
              })
            }) : null, /*#__PURE__*/jsx("div", {
              style: {
                flex: "1 1 100%",
                overflowY: "auto",
                overflowX: "hidden"
              },
              children: /*#__PURE__*/jsx(Listbox, {
                "aria-labelledby": ariaLabelledBy,
                enableSelectAll: enableSelectAll,
                endAdornment: endAdornment,
                filterOptions: filterOptions,
                id: listboxId,
                multi: multi,
                onKeyDown: handleListboxKeyDown,
                onSelectedChange: handleListboxChange,
                optionHeight: optionHeight,
                options: options,
                ref: listRef,
                renderOption: renderOption,
                selectedOptions: selectedOptions,
                scrollRef: boxRef,
                startAdornment: startAdornment,
                onFinalListItem: handleFinalListItem,
                onFirstListItem: handleFirstListItem
              })
            }), footer && /*#__PURE__*/jsx(FooterWrapper, {
              tabIndex: -1,
              ref: footerRef,
              onKeyDown: handleFooterNavigation,
              children: footer
            })]
          })
        });
      }
    })
  }));
});
Select.displayName = "Select";

export { Select };
